import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Divider,
  CircularProgress,
  TextField,
  MenuItem,
  Button,
} from "@mui/material";
import { getStagedQuestions } from "../../../../api/auth";
import { releaseStagedQuestions } from "../../../../api/auth";
import { useSelector } from "react-redux";

const allowedEmails = [
  "rchoudhary@preclinique.co.uk",
  "yusufm2000@hotmail.co.uk",
  "htahir@preclinique.co.uk",
 // "pabone1112@degcos.com",
  "ymaududi@preclinique.co.uk",
];

const safeParse = (v) => {
  try {
    return typeof v === "string" ? JSON.parse(v) : v;
  } catch {
    return null;
  }
};

const normalizeStaged = (q) => ({
  ...q,
  choices: Array.isArray(q.choices) ? q.choices : safeParse(q.choices) ?? [],
  child_question_ids: Array.isArray(q.child_question_ids)
    ? q.child_question_ids
    : safeParse(q.child_question_ids) ?? [],
});

const StagedPreview = () => {
  const userEmail = useSelector((state) => state.auth.email);
  const isAllowed = allowedEmails.includes(userEmail?.toLowerCase());
  const [stagedQuestions, setStagedQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [universities, setUniversities] = useState([]);
  const [courses, setCourses] = useState([]);
  const [categories, setCategories] = useState([]);

  const [filters, setFilters] = useState({
    university: "",
    course: "",
    category: "",
  });

  const fetchQuestions = async () => {
    try {
      const queryParams = new URLSearchParams();

      if (filters.university)
        queryParams.append("university", filters.university);
      if (filters.course) queryParams.append("course", filters.course);
      if (filters.category) queryParams.append("category", filters.category);

      const response = await getStagedQuestions(queryParams.toString());
      setStagedQuestions((response.stagedQuestions || []).map(normalizeStaged));
    } catch (error) {
      console.error("Error fetching staged questions:", error.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const fetchAll = async () => {
      try {
        const response = await getStagedQuestions(); // one call only
        const questions = response.stagedQuestions || [];
        setStagedQuestions(questions.map(normalizeStaged));

        // Extract dropdown options
        const uniSet = new Set();
        const courseSet = new Set();
        const categorySet = new Set();

        questions.forEach((q) => {
          if (q.university_name) uniSet.add(q.university_name);
          if (q.course) courseSet.add(q.course);
          if (q.category_name) categorySet.add(q.category_name);
        });

        setUniversities([...uniSet]);
        setCourses([...courseSet]);
        setCategories([...categorySet]);
      } catch (err) {
        console.error("Error fetching staged questions:", err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchAll();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({ ...prev, [name]: value }));
  };

  console.log("User email:", userEmail);
  console.log("Is allowed:", isAllowed);

  return (
    <Box sx={{ padding: "20px" }}>
      <Typography variant="h4" gutterBottom>
        Preview of Staged Questions
      </Typography>

      {isAllowed && (
        <Box sx={{ display: "flex", gap: 2, marginBottom: 2 }}>
          <TextField
            select
            label="University"
            name="university"
            value={filters.university}
            onChange={handleChange}
            size="small"
          >
            <MenuItem value="">All</MenuItem>
            {universities.map((uni) => (
              <MenuItem key={uni} value={uni}>
                {uni}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            select
            label="Course"
            name="course"
            value={filters.course}
            onChange={handleChange}
            size="small"
          >
            <MenuItem value="">All</MenuItem>
            {courses.map((course) => (
              <MenuItem key={course} value={course}>
                {course}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            select
            label="Category"
            name="category"
            value={filters.category}
            onChange={handleChange}
            size="small"
          >
            <MenuItem value="">All</MenuItem>
            {categories.map((cat) => (
              <MenuItem key={cat} value={cat}>
                {cat}
              </MenuItem>
            ))}
          </TextField>
        </Box>
      )}

      {isAllowed && filters.university && (
        <Button
          variant="contained"
          color="secondary"
          sx={{ mb: 2 }}
          onClick={async () => {
            const confirmed = window.confirm(
              `Are you sure you want to release the questions for ${filters.university} to the real question bank?`
            );
            if (confirmed) {
              try {
                await releaseStagedQuestions(filters.university);
                alert(
                  `Successfully released all questions for ${filters.university}`
                );
                fetchQuestions(); // re-fetch to update UI
              } catch (err) {
                console.error(err);
                alert("Failed to release questions.");
              }
            }
          }}
        >
          Release All for {filters.university}
        </Button>
      )}

      {loading ? (
        <CircularProgress />
      ) : stagedQuestions.length === 0 ? (
        <Typography>No questions currently in the staging area.</Typography>
      ) : (
        stagedQuestions
          .filter((q) => {
            return (
              (!filters.university ||
                q.university_name === filters.university) &&
              (!filters.course || q.course === filters.course) &&
              (!filters.category || q.category_name === filters.category)
            );
          })
          .map((question) => (
            <Card
              key={question.staged_id}
              sx={{
                marginBottom: "20px",
                border: "1px solid #ddd",
                padding: "16px",
              }}
            >
              <CardContent>
                <Typography variant="h6">
                  Question: {question.question_text}
                      </Typography>
                      {/* Conditional rendering for the image link */}
                      {question.image ? (
                          // Render a hyperlink if an image link exists
                          <Typography variant="subtitle1" color="textSecondary">
                              Image included for this question,&nbsp;
                              <a
                                  href={question.image}
                                  target="_blank" // Opens the link in a new tab
                                  rel="noopener noreferrer" // Security best practice for links
                                  style={{
                                      color: "blue",
                                      textDecoration: "underline",
                                      cursor: "pointer",
                                  }}
                              >
                                  click here to view.
                              </a>
                          </Typography>
                      ) : (
                          // Render a simple message if no image link exists
                          <Typography variant="subtitle1" color="textSecondary">
                              No image for this question.
                          </Typography>
                      )}
                <Typography variant="subtitle2" color="textSecondary">
                  Lecture: {question.lecture_reference}
                </Typography>
                {question.image && (
                  <Box sx={{ mt: 1 }}>
                    <img
                      src={question.image}
                      alt="Question"
                      style={{ maxWidth: "100%", borderRadius: 6 }}
                    />
                  </Box>
                )}

                <Typography variant="subtitle2" color="textSecondary">
                  Category: {question.category_name} | Subcategory:{" "}
                  {question.subcategory_name}
                </Typography>
                <Typography variant="subtitle2" color="textSecondary">
                  Course: {question.course} | University:{" "}
                  {question.university_name}
                </Typography>

                {/* Type label (optional but helpful) */}
                <Typography variant="subtitle2" color="textSecondary">
                  Type:{" "}
                  {question.type === 1
                    ? "MCQ"
                    : question.type === 2
                    ? "VSAQ"
                    : question.type === 3
                    ? "SAQ"
                    : "Unknown"}
                </Typography>

                {/* SAQ (child parts) */}
                {Array.isArray(question.child_question_ids) &&
                  question.child_question_ids.length > 0 && (
                    <Box sx={{ mt: 1 }}>
                      <Typography variant="subtitle1">
                        SAQ Sub-questions
                      </Typography>
                      {question.child_question_ids.map((sq, idx) => {
                        const qKey = Object.keys(sq || {}).find(
                          (k) =>
                            k.startsWith("question_") && k.endsWith("_text")
                        );
                        const letter = qKey
                          ? (qKey.split("_")[1] || "").toUpperCase()
                          : String.fromCharCode(66 + idx); // B, C, D...

                        return (
                          <Box
                            key={idx}
                            sx={{ pl: 2, borderLeft: "3px solid #eee", mb: 1 }}
                          >
                            <Typography
                              variant="body2"
                              sx={{ whiteSpace: "pre-wrap" }}
                            >
                              <strong>Part {letter}:</strong>{" "}
                              {qKey ? sq[qKey] : ""}
                            </Typography>

                            {!!sq.image && (
                              <Box sx={{ mt: 0.5 }}>
                                <img
                                  src={sq.image}
                                  alt={`SAQ part image`}
                                  style={{ maxWidth: "100%", borderRadius: 6 }}
                                />
                              </Box>
                            )}

                            {!!sq.correct_answer && (
                              <Typography
                                variant="body2"
                                sx={{ whiteSpace: "pre-wrap", mt: 0.5 }}
                              >
                                <strong>Answer:</strong> {sq.correct_answer}
                              </Typography>
                            )}
                          </Box>
                        );
                      })}
                    </Box>
                  )}

                {Array.isArray(question.choices) &&
                  question.choices.length > 0 && (
                    <Box sx={{ marginTop: "10px" }}>
                      {question.choices.map((choice, index) => (
                        <Box key={index} sx={{ marginBottom: "10px" }}>
                          <Typography>
                            Choice {index + 1}: {choice.option_text}{" "}
                            {choice.is_correct && (
                              <span style={{ color: "green" }}>(Correct)</span>
                            )}
                          </Typography>
                          {choice.explanation_text && (
                            <Typography variant="body2" color="textSecondary">
                              Explanation: {choice.explanation_text}
                            </Typography>
                          )}
                        </Box>
                      ))}
                    </Box>
                  )}
              </CardContent>
              <Divider sx={{ marginY: "10px" }} />
            </Card>
          ))
      )}
    </Box>
  );
};

export default StagedPreview;
